import { isValidationError } from '@/lib/errors';
import { getLogEnvironment, shouldPersistLog } from '@/lib/platform/env';

/**
 * Padroes de erro que sao RUIDO (nao bugs) e devem ser descartados
 * antes de virar notificacao / entrar em platform_logs.
 *
 * Filtramos por TIPO quando possivel (ValidationError, AbortError). As
 * checagens por string existem como fallback pra erros originados fora
 * do nosso codigo (libs de terceiros, runtime do browser).
 *
 * - ValidationError: regra de negocio exibida ao usuario via toast. Se
 *   bubbled pra unhandledrejection, ainda assim nao e bug de sistema.
 * - AbortError / "signal is aborted": fetch cancelado por navegacao/unmount
 *   (normal em React Query ao desmontar componentes).
 * - "Component unmounted": callback tardio de React Query.
 * - "The user aborted a request": variantes em Safari/Firefox.
 * - "Lock ... was released because another request stole it": supabase-js
 *   registra unhandled rejection quando dois getUser() correm em paralelo
 *   (multi-tab/StrictMode). Benigno — o segundo call re-adquire o lock.
 * - React errors 418/423/425: hydration mismatches quase sempre causados
 *   por extensao de browser, Chrome auto-translate ou Grammarly mutando
 *   DOM. Nao ha acao do lado do app.
 */
function isNoiseError(err: unknown): boolean {
  if (isValidationError(err)) return true;
  const e = err as { name?: string; message?: string } | null;
  if (!e) return false;
  if (e.name === 'AbortError' || e.name === 'CanceledError') return true;
  const msg = (e.message || '').toLowerCase();
  if (msg.includes('signal is aborted')) return true;
  if (msg.includes('the user aborted a request')) return true;
  if (msg.includes('component unmounted')) return true;
  if (msg.includes('aborterror')) return true;
  // supabase-js: lock contention entre tabs/strict mode
  if (msg.includes('lock ') && msg.includes('was released because another request stole it')) return true;
  // React hydration — causa quase sempre externa (extension / translate)
  if (msg.includes('minified react error #418')) return true;
  if (msg.includes('minified react error #423')) return true;
  if (msg.includes('minified react error #425')) return true;
  if (msg.includes('hydration failed') || msg.includes('text content does not match')) return true;
  // "Failed to fetch" originado de extensões de browser (ex: antivírus injetando sobre window.fetch)
  // Verificamos o stack porque "Failed to fetch" legítimo (API down) não tem stack de chrome-extension
  const stack = ((err as { stack?: string })?.stack || '').toLowerCase();
  if (msg === 'failed to fetch' && stack.includes('chrome-extension://')) return true;
  // removeChild/insertBefore vindos de browser extensions (Google Translate, Grammarly).
  // Sinal: stack só tem React internals + sem código nosso (/src/, /app/, /features/).
  // Se tiver código nosso na stack, pode ser bug real — não filtra.
  if (/removechild.*not a child|insertbefore.*not a child/i.test(msg)) {
    const hasOurCode = /\/src\/|\/app\/|\/features\/|\/lib\//.test(stack);
    if (!hasOurCode) return true;
  }
  // Turbopack HMR: chunk obsoleto após hot-reload em dev (stale module factory)
  if (msg.includes('module factory is not available')) return true;
  // Turbopack HMR: erro de RSC streaming durante hot-reload em dev
  if (msg.includes('chunk.reason.enqueuemodel is not a function')) return true;
  // Turbopack HMR: variante moderna do mesmo bug (chunk vira null durante hot-reload)
  if (msg.includes("cannot read properties of null") && msg.includes("enqueuemodel")) return true;
  // Turbopack HMR: ReferenceError de ícone/função ainda não definida no ciclo de hot-reload
  // Padrão: "X is not defined" com performReactRefresh na stack (sempre dev)
  if (stack.includes('performreactrefresh') && msg.endsWith('is not defined')) return true;
  // Async client component: erro de dev ao adicionar async em Client Component
  if (msg.includes('is an async client component')) return true;
  // Stale chunk pós-deploy: usuário com aba aberta no momento do deploy
  // Não há fix de código — reload resolve. Filtramos pois não são acionáveis.
  if (msg.startsWith('failed to load chunk')) return true;
  return false;
}

/**
 * Reporta erros do cliente para /api/platform/log.
 * Nunca lanca — falhas de logging sao silenciadas.
 * Retorna early se o erro for "ruido" conhecido (aborts de navegacao).
 */
export async function reportClientError(
  error: unknown,
  meta: {
    level?: 'warning' | 'error' | 'critical';
    context?: Record<string, unknown>;
    fingerprint?: string;
  } = {}
): Promise<void> {
  try {
    if (isNoiseError(error)) return;
    const level = meta.level ?? 'error';
    const environment = getLogEnvironment();
    // Curto-circuito no client: se for dev e level<critical, nem manda pro servidor
    // (economiza request HTTP).
    if (!shouldPersistLog(level, environment)) return;

    const err = error instanceof Error ? error : new Error(String(error));
    const body = JSON.stringify({
      level,
      message: err.message || 'Client error',
      stack: err.stack ?? null,
      errorName: err.name || 'Error',
      context: meta.context ?? {},
      fingerprint: meta.fingerprint,
      environment,
      url: typeof window !== 'undefined' ? window.location.href : null,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    });

    if (typeof navigator !== 'undefined' && 'sendBeacon' in navigator) {
      const blob = new Blob([body], { type: 'application/json' });
      const sent = navigator.sendBeacon('/api/platform/log', blob);
      if (sent) return;
    }

    await fetch('/api/platform/log', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body,
      keepalive: true,
    });
  } catch {
    /* engole */
  }
}

/**
 * Instala handlers globais em window (unhandled errors + promise rejections).
 * Chamar 1x no root layout / ClientProviders.
 */
let installed = false;
function isChunkError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { name?: string; message?: string };
  const msg = e.message ?? '';
  return (
    e.name === 'ChunkLoadError' ||
    msg.includes('Loading chunk') ||
    msg.includes('Failed to load chunk') ||
    msg.includes('module factory is not available') ||
    msg.includes('Unexpected token')
  );
}

export function installGlobalErrorReporter(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event) => {
    if (isChunkError(event.error)) {
      window.location.reload();
      return;
    }
    void reportClientError(event.error ?? event.message, {
      level: 'error',
      context: {
        type: 'window.onerror',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkError(event.reason)) {
      window.location.reload();
      return;
    }
    void reportClientError(event.reason, {
      level: 'error',
      context: { type: 'unhandledrejection' },
    });
  });
}
